/**
 * Stability Tracker
 *
 * Regras portadas da POC:
 * - Exatamente 1 rosto estável por 2000ms dispara estabilidade.
 * - Mudança de face: maxCenterDelta=0.12, maxSizeDelta=0.20 (bounding box normalizado).
 * - Ausência confirmada após 2 amostras vazias consecutivas.
 * - Múltiplos rostos = instável imediato.
 */

export interface FaceBox {
  xCenter: number; // 0..1
  yCenter: number; // 0..1
  width: number; // 0..1
  height: number; // 0..1
}

export type StabilityEvent =
  | { type: "stable"; box: FaceBox }
  | { type: "unstable" }
  | { type: "absent" }
  | { type: "multiple" };

export interface StabilityTrackerOptions {
  stableMs?: number;
  maxCenterDelta?: number;
  maxSizeDelta?: number;
  emptySamplesForAbsence?: number;
}

export class StabilityTracker {
  private readonly stableMs: number;
  private readonly maxCenterDelta: number;
  private readonly maxSizeDelta: number;
  private readonly emptySamplesForAbsence: number;

  private anchor: FaceBox | null = null;
  private stableSince = 0;
  private emptySamples = 0;
  private lastEvent: StabilityEvent | null = null;
  private nowFn: () => number;

  constructor(options: StabilityTrackerOptions = {}, nowFn: () => number = Date.now) {
    this.stableMs = options.stableMs ?? 2000;
    this.maxCenterDelta = options.maxCenterDelta ?? 0.12;
    this.maxSizeDelta = options.maxSizeDelta ?? 0.20;
    this.emptySamplesForAbsence = options.emptySamplesForAbsence ?? 2;
    this.nowFn = nowFn;
  }

  /**
   * Processa uma amostra de detecção.
   * @param boxes Array de bounding boxes detectadas neste frame (normalizadas).
   * @returns Evento de estabilidade emitido para este frame.
   */
  feed(boxes: FaceBox[]): StabilityEvent {
    const now = this.nowFn();

    if (boxes.length === 0) {
      this.emptySamples++;
      if (this.emptySamples >= this.emptySamplesForAbsence) {
        this.reset();
        this.lastEvent = { type: "absent" };
        return this.lastEvent;
      }
      // Ainda não atingiu ausência confirmada: mantém último estado conhecido,
      // mas não avança estabilidade.
      if (this.lastEvent?.type === "stable") {
        // Continua estável até ausência confirmada
        return this.lastEvent;
      }
      this.lastEvent = { type: "unstable" };
      return this.lastEvent;
    }

    this.emptySamples = 0;

    if (boxes.length > 1) {
      this.reset();
      this.lastEvent = { type: "multiple" };
      return this.lastEvent;
    }

    const box = boxes[0]!;

    if (!this.anchor) {
      this.anchor = box;
      this.stableSince = now;
      this.lastEvent = { type: "unstable" };
      return this.lastEvent;
    }

    if (this.faceChanged(this.anchor, box)) {
      this.anchor = box;
      this.stableSince = now;
      this.lastEvent = { type: "unstable" };
      return this.lastEvent;
    }

    const elapsed = now - this.stableSince;
    if (elapsed >= this.stableMs) {
      this.lastEvent = { type: "stable", box };
      return this.lastEvent;
    }

    this.lastEvent = { type: "unstable" };
    return this.lastEvent;
  }

  reset(): void {
    this.anchor = null;
    this.stableSince = 0;
    this.emptySamples = 0;
    this.lastEvent = null;
  }

  private faceChanged(a: FaceBox, b: FaceBox): boolean {
    const centerDelta = Math.hypot(a.xCenter - b.xCenter, a.yCenter - b.yCenter);
    const sizeDelta = Math.abs(a.width - b.width) + Math.abs(a.height - b.height);
    return centerDelta > this.maxCenterDelta || sizeDelta > this.maxSizeDelta;
  }
}
